import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import {
  ERROR_CATEGORIES,
  SEVERITIES,
} from '../constants/reviewCatalog';
import { validateClassification } from '../utils/reviewGuardrails';
import { formatLockOwnerLabel } from '../utils/translationLock';
import { getTargetLabel, getSourceLabel, getTmDisplayName, getLanguagePairLabel, resolveTmId } from '../utils/languages';
import { isTranslated } from '../utils/status';
import { extractJsonLeaves, buildJsonFromLeaves, applyLeafValues } from '../utils/jsonDocument';
import { buildDisplayModels, toStorageHtml, containsHtml } from '../utils/htmlMarkers';
import {
  buildEditTooltipLabel,
  computeChangedSpan,
} from '../utils/draftState';
import ApproveSaveDropdown from './ApproveSaveDropdown';
import CopyButton from './CopyButton';
import MarkerRichText from './MarkerRichText';
import MarkerEditableCell from './MarkerEditableCell';

function leafKeyLabel(path) {
  const segments = String(path || '').split('.').filter(Boolean);
  const key = segments.length ? segments[segments.length - 1] : path;
  return `"${key}"`;
}

function tryParseJson(text) {
  if (!text || typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (trimmed[0] !== '{' && trimmed[0] !== '[') return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === 'object' && parsed !== null) return parsed;
  } catch { /* not JSON */ }
  return null;
}

function getLeafValueFromDraftTarget(draftTarget, path) {
  const parsed = tryParseJson(draftTarget);
  if (!parsed) return '';
  const leaves = extractJsonLeaves(parsed);
  const leaf = leaves.find((entry) => entry.path === path);
  return leaf ? leaf.value : '';
}

function hasActiveDraft(request) {
  return request.draftStatus === 'in_progress' && !!request.draftTarget;
}

function buildLeafEditsFromRequest(request, source, aiTarget, isJsonContent) {
  if (!isJsonContent) return {};
  const edits = {};
  const parsed = tryParseJson(source);
  if (!parsed) return edits;
  const leaves = extractJsonLeaves(parsed);
  const seedTarget = hasActiveDraft(request) ? request.draftTarget : aiTarget;
  const tParsed = tryParseJson(seedTarget);
  const tLeaves = tParsed ? extractJsonLeaves(tParsed) : [];
  const tMap = {};
  for (const tl of tLeaves) tMap[tl.path] = tl.value;
  for (const leaf of leaves) {
    edits[leaf.path] = tMap[leaf.path] || '';
  }
  return edits;
}

function buildEditedTargetFromRequest(request, aiTarget) {
  return hasActiveDraft(request) ? request.draftTarget : aiTarget;
}

function RequestDetail({
  request,
  onClose,
  onSave,
  onSaveDraft,
  onSaveAndLock,
  availableTMs = [],
  readOnly = false,
  lockInfo = null,
  resourceType,
  parentId,
  onNotifyLockWarning,
}) {
  const aiTarget = request.target || request.Spanish || '';
  const hasDraft = hasActiveDraft(request);
  const [editedTarget, setEditedTarget] = useState(() => (
    buildEditedTargetFromRequest(request, aiTarget)
  ));
  const [saving, setSaving] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [hasUnsavedChangesSinceDraft, setHasUnsavedChangesSinceDraft] = useState(false);
  const lastTouchedPathRef = useRef(request.draftLastEditPath || '$root');
  const [notes, setNotes] = useState(request.notes || '');
  const [error, setError] = useState(null);
  const errorBannerRef = useRef(null);
  const [errorCategory, setErrorCategory] = useState(request.error_category || '');
  const [severity, setSeverity] = useState(request.severity || '');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const approveButtonRef = useRef(null);

  const source = request.source || request.English || '';
  const status = request.status?.toLowerCase();
  const statusMessage = request.message || request.error_message || '';
  const isReadyForReview = isTranslated(status);
  const isRetryPending = status === 'retry_pending';
  const isError = status === 'error';
  const hasTarget = !!aiTarget;
  const isReviewed = request.review_status === 'approved';
  const resolvedTmId = resolveTmId(request, availableTMs);
  const tmDisplayName = getTmDisplayName(resolvedTmId, availableTMs);
  const languagePairLabel = getLanguagePairLabel(request);

  const originalAITranslation = request.original_translation || aiTarget;
  const savedTarget = request.target || request.Spanish || '';
  const savedNotes = request.notes || '';
  const savedErrorCategory = request.error_category || '';
  const savedSeverity = request.severity || '';
  const lockedByLabel = formatLockOwnerLabel(lockInfo);
  const editingDisabled = readOnly;
  const isLocked = Boolean(lockInfo?.isLocked);
  const emptyReadOnlyValue = '—';

  // JSON leaf editing: detect JSON content and break into editable leaves
  const isJsonContent = request.content_type === 'json' || (tryParseJson(source) !== null && tryParseJson(aiTarget) !== null);
  const sourceLeaves = useMemo(() => {
    if (!isJsonContent) return [];
    const parsed = tryParseJson(source);
    return parsed ? extractJsonLeaves(parsed) : [];
  }, [isJsonContent, source]);

  const targetLeaves = useMemo(() => {
    if (!isJsonContent) return [];
    const parsed = tryParseJson(aiTarget);
    return parsed ? extractJsonLeaves(parsed) : [];
  }, [isJsonContent, aiTarget]);

  // Build a map of path → translated value for the AI translation
  const targetByPath = useMemo(() => {
    const map = {};
    for (const leaf of targetLeaves) {
      map[leaf.path] = leaf.value;
    }
    return map;
  }, [targetLeaves]);

  // Editable leaf values — one entry per source leaf path
  const [leafEdits, setLeafEdits] = useState(() => (
    buildLeafEditsFromRequest(request, source, aiTarget, isJsonContent)
  ));

  const editTooltipLabel = useMemo(
    () => buildEditTooltipLabel(
      request.draftEditedByName,
      request.draftEditedBy,
      request.draftEditedAt,
    ),
    [request.draftEditedByName, request.draftEditedBy, request.draftEditedAt],
  );

  const buildHighlightForPath = useCallback((path) => {
    if (!hasDraft || request.draftLastEditPath !== path) return null;
    const start = request.draftLastEditStart;
    const end = request.draftLastEditEnd;
    if (typeof start !== 'number' || typeof end !== 'number' || end <= start) return null;
    return { start, end, tooltipLabel: editTooltipLabel };
  }, [
    hasDraft,
    request.draftLastEditPath,
    request.draftLastEditStart,
    request.draftLastEditEnd,
    editTooltipLabel,
  ]);

  useEffect(() => {
    if (request.draftLastEditPath) {
      lastTouchedPathRef.current = request.draftLastEditPath;
    }
  }, [request.draftLastEditPath]);

  const {
    draftStatus: requestDraftStatus,
    draftTarget: requestDraftTarget,
    draftEditedAt: requestDraftEditedAt,
    draftLastEditPath: requestDraftLastEditPath,
    target: requestTargetField,
    Spanish: requestSpanishField,
    notes: requestNotes,
    error_category: requestErrorCategory,
    severity: requestSeverity,
    req_id: requestId,
  } = request;

  const aiTargetFromRequest = requestTargetField || requestSpanishField || '';

  const draftSnapshotKey = [
    requestId,
    requestDraftStatus || '',
    requestDraftEditedAt || '',
    requestDraftTarget || '',
    aiTargetFromRequest,
  ].join('|');

  useEffect(() => {
    const draftCtx = {
      draftStatus: requestDraftStatus,
      draftTarget: requestDraftTarget,
    };
    setEditedTarget(buildEditedTargetFromRequest(draftCtx, aiTargetFromRequest));
    setLeafEdits(buildLeafEditsFromRequest(draftCtx, source, aiTargetFromRequest, isJsonContent));
    setMarkerEdits({});
    setHasUnsavedChangesSinceDraft(false);
    setNotes(requestNotes || '');
    setErrorCategory(requestErrorCategory || '');
    setSeverity(requestSeverity || '');
    if (requestDraftLastEditPath) {
      lastTouchedPathRef.current = requestDraftLastEditPath;
    }
  }, [
    draftSnapshotKey,
    source,
    isJsonContent,
    requestDraftStatus,
    requestDraftTarget,
    aiTargetFromRequest,
    requestNotes,
    requestErrorCategory,
    requestSeverity,
    requestDraftLastEditPath,
  ]);

  // Make validation/save failures impossible to miss: when Approve & Save /
  // Save & Lock is blocked (e.g. by the Error Category/Severity
  // classification requirement) or a save/lock API call fails, ensure the
  // banner is scrolled into view and focused so the action doesn't appear to
  // silently "do nothing."
  useEffect(() => {
    if (error && errorBannerRef.current) {
      if (typeof errorBannerRef.current.scrollIntoView === 'function') {
        errorBannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      if (typeof errorBannerRef.current.focus === 'function') {
        errorBannerRef.current.focus({ preventScroll: true });
      }
    }
  }, [error]);

  const handleLeafEdit = (path, value) => {
    lastTouchedPathRef.current = path;
    setHasUnsavedChangesSinceDraft(true);
    setLeafEdits((prev) => ({ ...prev, [path]: value }));
  };

  // HTML inside JSON string values: leaves that contain HTML tags are shown
  // with numbered marker chips (tooltip = original tag). Leaves without HTML
  // keep the plain textarea. Placeholders such as {{name}}, {0} and ${name}
  // are left as literal text.
  const htmlLeafModels = useMemo(() => {
    const models = {};
    for (const leaf of sourceLeaves) {
      const aiVal = targetByPath[leaf.path] || '';
      // The "edit" display must reflect the current editable value for this
      // leaf (leafEdits — seeded from the saved draft when one exists, else
      // the AI translation), NOT unconditionally the AI translation. This is
      // only used before the user has actively edited the leaf in the
      // current session (markerEdits[path] takes priority once they have),
      // so it governs the initial/restored display shown for every leaf,
      // including ones the draft touched but this session hasn't yet.
      const editVal = leafEdits[leaf.path] ?? aiVal;
      const { source: srcModel, translation: aiModel } = buildDisplayModels(leaf.value, aiVal);
      const { translation: editModel } = editVal === aiVal
        ? { translation: aiModel }
        : buildDisplayModels(leaf.value, editVal);
      models[leaf.path] = {
        sourceDisplay: srcModel.displayWithMarkers,
        aiDisplay: aiModel.displayWithMarkers,
        editDisplay: editModel.displayWithMarkers,
        sourceTagMap: srcModel.tagMap,
        sourceEntityMap: srcModel.entityMap,
        tagMap: aiModel.tagMap,
        entityMap: aiModel.entityMap,
        editTagMap: editModel.tagMap,
        editEntityMap: editModel.entityMap,
      };
    }
    return models;
  }, [sourceLeaves, targetByPath, leafEdits]);

  // Live marker-display state for HTML leaves the user has touched.
  const [markerEdits, setMarkerEdits] = useState({});

  // Marker display is converted back to storage HTML with toStorageHtml so the
  // saved leaf value (and the reassembled JSON target) contains real tags.
  const handleLeafMarkerEdit = (path, displayWithMarkers, tagMap, entityMap) => {
    lastTouchedPathRef.current = path;
    setHasUnsavedChangesSinceDraft(true);
    setMarkerEdits((prev) => ({
      ...prev,
      [path]: { display: displayWithMarkers, tagMap, entityMap },
    }));
    handleLeafEdit(path, toStorageHtml(displayWithMarkers, tagMap, entityMap).trim());
  };

  // Reassemble edited JSON from leaf edits
  const reassembledTarget = useMemo(() => {
    if (!isJsonContent || sourceLeaves.length === 0) return editedTarget;
    const leaves = sourceLeaves.map((sl) => ({
      source_path: sl.path,
      value: leafEdits[sl.path] ?? '',
    }));
    try {
      return JSON.stringify(buildJsonFromLeaves(leaves), null, 2);
    } catch {
      return editedTarget;
    }
  }, [isJsonContent, sourceLeaves, leafEdits, editedTarget]);

  const parsedSourceJson = useMemo(() => tryParseJson(source), [source]);

  const copyJsonSource = useMemo(() => {
    if (!parsedSourceJson) return source;
    return JSON.stringify(parsedSourceJson, null, 2);
  }, [parsedSourceJson, source]);

  const copyJsonAi = useMemo(() => {
    if (!parsedSourceJson) return aiTarget;
    return JSON.stringify(
      applyLeafValues(
        parsedSourceJson,
        targetLeaves.map((leaf) => ({ path: leaf.path, value: leaf.value })),
      ),
      null,
      2,
    );
  }, [parsedSourceJson, aiTarget, targetLeaves]);

  const copyJsonEdit = useMemo(() => {
    if (!parsedSourceJson) return reassembledTarget;
    return JSON.stringify(
      applyLeafValues(
        parsedSourceJson,
        sourceLeaves.map((leaf) => ({
          path: leaf.path,
          value: leafEdits[leaf.path] ?? '',
        })),
      ),
      null,
      2,
    );
  }, [parsedSourceJson, reassembledTarget, sourceLeaves, leafEdits]);

  // For JSON: check if any leaf was edited vs AI translation
  const jsonWasEdited = useMemo(() => {
    if (!isJsonContent) return false;
    for (const leaf of sourceLeaves) {
      const aiVal = targetByPath[leaf.path] || '';
      const editedVal = leafEdits[leaf.path] || '';
      if (editedVal.trim() !== aiVal.trim()) return true;
    }
    return false;
  }, [isJsonContent, sourceLeaves, targetByPath, leafEdits]);

  const wasEdited = isJsonContent ? jsonWasEdited : editedTarget.trim() !== originalAITranslation.trim();
  const displayWasEdited = savedTarget.trim() !== originalAITranslation.trim();

  const buildSavePayload = useCallback(() => {
    const finalTarget = isJsonContent ? reassembledTarget : editedTarget.trim();
    // For JSON: send the reassembled target as original_translation when no leaf
    // was edited, so the backend string comparison sees them as identical and
    // doesn't require error_category/severity.
    const origTranslation = (isJsonContent && !jsonWasEdited) ? finalTarget : originalAITranslation;
    return {
      source: source.trim(),
      target: finalTarget,
      sourceLanguage: request.sourceLanguage || 'en-US',
      targetLanguage: request.targetLanguage || 'es-US',
      category: request.category || 'linguist-approved',
      program: request.program || 'domestic',
      tm_id: resolvedTmId,
      notes: notes.trim() || null,
      req_id: request.req_id,
      original_translation: origTranslation,
      error_category: wasEdited ? (errorCategory || null) : null,
      severity: wasEdited ? (severity || null) : null,
      content_type: isJsonContent ? 'json' : '',
    };
  }, [
    source,
    editedTarget,
    reassembledTarget,
    isJsonContent,
    jsonWasEdited,
    request,
    resolvedTmId,
    notes,
    originalAITranslation,
    wasEdited,
    errorCategory,
    severity,
  ]);

  const validateBeforeSave = () => {
    if (editingDisabled) return 'Editing is disabled.';
    const hasEditedContent = isJsonContent ? sourceLeaves.length > 0 : !!editedTarget.trim();
    if (!hasEditedContent || !source.trim()) {
      return 'Source and target text are required.';
    }
    return validateClassification({ wasEdited, errorCategory, severity });
  };

  const handleApprove = async () => {
    const validationError = validateBeforeSave();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSave(request.req_id, buildSavePayload());
    } catch (err) {
      setError('Failed to save translation: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const computeDraftEditRange = useCallback(() => {
    const touchedPath = lastTouchedPathRef.current || '$root';
    if (isJsonContent && sourceLeaves.length > 0) {
      const path = sourceLeaves.some((leaf) => leaf.path === touchedPath)
        ? touchedPath
        : sourceLeaves[0].path;
      const leaf = sourceLeaves.find((entry) => entry.path === path);
      if (!leaf) return { path: '$root', start: null, end: null };
      const aiVal = targetByPath[leaf.path] || '';
      const editVal = leafEdits[leaf.path] || '';
      const priorVal = hasDraft && request.draftTarget
        ? getLeafValueFromDraftTarget(request.draftTarget, leaf.path)
        : aiVal;
      const htmlModel = htmlLeafModels[leaf.path];
      const markerEdit = markerEdits[leaf.path];
      const useMarkerUnits = Boolean(
        htmlModel && (containsHtml(leaf.value) || containsHtml(editVal) || containsHtml(priorVal)),
      );
      if (useMarkerUnits && htmlModel) {
        // "before" must always reflect priorVal's display (AI translation when
        // there is no existing draft; the prior draft value otherwise) — NOT
        // htmlModel.editDisplay, which now reflects the CURRENT edit value.
        const beforeDisplay = hasDraft && request.draftTarget
          ? buildDisplayModels(leaf.value, priorVal).translation.displayWithMarkers
          : htmlModel.aiDisplay;
        const afterDisplay = markerEdit?.display
          ?? buildDisplayModels(leaf.value, editVal).translation.displayWithMarkers;
        const span = computeChangedSpan(beforeDisplay, afterDisplay, { useMarkerUnits: true });
        return { path, ...(span || { start: null, end: null }) };
      }
      const span = computeChangedSpan(priorVal, editVal, { useMarkerUnits: false });
      return { path, ...(span || { start: null, end: null }) };
    }
    const prior = hasDraft ? request.draftTarget : aiTarget;
    const next = editedTarget.trim();
    const span = computeChangedSpan(prior, next, { useMarkerUnits: false });
    return { path: '$root', ...(span || { start: null, end: null }) };
  }, [
    isJsonContent,
    sourceLeaves,
    targetByPath,
    leafEdits,
    hasDraft,
    request.draftTarget,
    htmlLeafModels,
    markerEdits,
    editedTarget,
    aiTarget,
  ]);

  const handleSaveDraft = async () => {
    if (!onSaveDraft || editingDisabled) return;
    const finalTarget = isJsonContent ? reassembledTarget : editedTarget.trim();
    if (!finalTarget) {
      setError('Nothing to save as draft.');
      return;
    }
    const range = computeDraftEditRange();
    setDraftSaving(true);
    setError(null);
    try {
      await onSaveDraft(request.req_id, {
        draft_target: finalTarget,
        content_type: isJsonContent ? 'json' : '',
        draft_last_edit_path: range.path || '$root',
        ...(typeof range.start === 'number' && typeof range.end === 'number'
          ? { draft_last_edit_start: range.start, draft_last_edit_end: range.end }
          : {}),
      });
      setHasUnsavedChangesSinceDraft(false);
    } catch (err) {
      setError('Failed to save draft: ' + (err.message || 'Unknown error'));
    } finally {
      setDraftSaving(false);
    }
  };

  const handleClose = () => {
    if (!readOnly && hasUnsavedChangesSinceDraft) {
      const proceed = window.confirm(
        'You have unsaved changes that have not been saved as a draft.\n\nClose without saving?',
      );
      if (!proceed) return;
    }
    onClose();
  };

  const handleSaveAndLock = async () => {
    const validationError = validateBeforeSave();
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!onSaveAndLock) return;

    setDropdownOpen(false);
    setSaving(true);
    setError(null);
    try {
      await onSave(request.req_id, buildSavePayload(), { keepOpen: true });
      try {
        await onSaveAndLock(request.req_id, { resourceType, parentId });
      } catch (lockErr) {
        if (onNotifyLockWarning) {
          onNotifyLockWarning(lockErr.message || 'Saved to TM but lock failed.');
        }
      }
    } catch (err) {
      setError('Failed to save translation: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const headerLabel = isReviewed
    ? 'Re-Review & Update'
    : (hasTarget && isReadyForReview ? 'Review & Approve' : 'Translation Request Details');

  const approveLabel = isReviewed ? 'Save Changes' : 'Approve & Save';
  const approveSubtext = isReviewed
    ? 'Saves a new version to Translation Memory and updates this request.'
    : 'Saves this translation to Translation Memory.';

  return (
    <div className="modal-overlay">
      <div className="modal modal--review" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{headerLabel}</h2>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>

        <div className={`modal-body${readOnly ? ' modal-body--locked-view' : ''}`}>
          <div className="request-context-strip" title={resolvedTmId}>
            <span><strong>Translation memory:</strong> {tmDisplayName || '—'}</span>
            <span className="request-context-sep">·</span>
            <span><strong>Language pair:</strong> {languagePairLabel}</span>
            <span className="request-context-sep">·</span>
            <span>
              <strong>Request ID:</strong>{' '}
              {request.req_id ? (
                <code className="request-context-id">{request.req_id}</code>
              ) : (
                '—'
              )}
            </span>
          </div>

          {readOnly && (
            <div className="review-status-banner review-lock-banner">
              <span className="review-icon">🔒</span>
              <div className="review-info">
                <div className="review-title">Translation Locked</div>
                <div className="review-meta">
                  This translation is currently locked by {lockedByLabel}. You can review it,
                  but only {lockedByLabel} can make changes.
                </div>
              </div>
            </div>
          )}

          {isLocked && !readOnly && (
            <div className="review-status-banner review-lock-banner review-lock-banner--mine">
              <span className="review-icon">🔒</span>
              <div className="review-info">
                <div className="review-title">Locked by You</div>
                <div className="review-meta">
                  This translation is locked. You can still update it; changes save a new TM version.
                </div>
              </div>
            </div>
          )}

          {isReviewed && !readOnly && !isLocked && (
            <div className="review-status-banner reviewed">
              <span className="review-icon">✓</span>
              <div className="review-info">
                <div className="review-title">Previously Reviewed</div>
                <div className="review-meta">
                  {request.reviewed_by && `By ${request.reviewed_by}`}
                  {request.reviewed_at && ` on ${new Date(request.reviewed_at).toLocaleDateString()}`}
                  . You can update this translation - your edits will be saved
                  as a new version.
                </div>
              </div>
            </div>
          )}

          {hasTarget && isReadyForReview && !isReviewed && !readOnly && (
            <div className="review-banner">
              <strong>Review &amp; Approve</strong>
              <p>Review the suggested translation below. Make edits if needed, then approve to save it for future use.</p>
            </div>
          )}

          {hasDraft && !readOnly && (
            <div className="editor-status-banner" role="status">
              <span className="editor-status-banner__icon" aria-hidden="true">⚠</span>
              <span>
                Currently being edited by{' '}
                <strong>{request.draftEditedByName || request.draftEditedBy}</strong>
              </span>
            </div>
          )}

          {hasTarget && isReadyForReview ? (
            <>
            {isJsonContent && sourceLeaves.length > 0 ? (
              /* ---- JSON leaf-level review ---- */
              <div className="json-leaf-review">
                <div className="json-leaf-summary">
                  JSON document · {sourceLeaves.length} translatable values
                  {wasEdited && <span className="review-editor-tag review-editor-tag--edited">Modified</span>}
                </div>
                <div
                  className="json-leaf-board"
                  style={{ gridTemplateRows: `auto repeat(${sourceLeaves.length}, auto)` }}
                >
                  <div
                    className="json-leaf-panel-header json-leaf-cell json-leaf-cell--col1 json-leaf-cell--header"
                    style={{ gridRow: 1, gridColumn: 1 }}
                  >
                    <div className="json-leaf-panel-title">Source Text ({getSourceLabel(request)})</div>
                    <CopyButton
                      value={copyJsonSource}
                      ariaLabel="Copy Source Text JSON"
                      className="review-pane-copy-btn json-leaf-panel-copy"
                      showLabel
                      label="Copy JSON"
                    />
                  </div>
                  <div
                    className="json-leaf-panel-header json-leaf-cell json-leaf-cell--col2 json-leaf-cell--header"
                    style={{ gridRow: 1, gridColumn: 2 }}
                  >
                    <div className="json-leaf-panel-title">
                      AI Translation ({getTargetLabel(request)})
                      <span className="review-editor-tag review-editor-tag--ai">AI</span>
                    </div>
                    <CopyButton
                      value={copyJsonAi}
                      ariaLabel="Copy AI Translation JSON"
                      className="review-pane-copy-btn json-leaf-panel-copy"
                      showLabel
                      label="Copy JSON"
                    />
                  </div>
                  <div
                    className="json-leaf-panel-header json-leaf-cell json-leaf-cell--col3 json-leaf-cell--header"
                    style={{ gridRow: 1, gridColumn: 3 }}
                  >
                    <div className="json-leaf-panel-title">
                      {readOnly ? 'Current Translation' : 'Edit Translation'}
                    </div>
                    <CopyButton
                      value={copyJsonEdit}
                      ariaLabel="Copy Edit Translation JSON"
                      className="review-pane-copy-btn json-leaf-panel-copy"
                      showLabel
                      label="Copy JSON"
                    />
                  </div>

                  {sourceLeaves.map((leaf, leafIndex) => {
                    const row = leafIndex + 2;
                    const isLastRow = leafIndex === sourceLeaves.length - 1;
                    const aiVal = targetByPath[leaf.path] || '';
                    const editVal = leafEdits[leaf.path] || '';
                    const leafModified = editVal.trim() !== aiVal.trim();
                    const htmlModel = htmlLeafModels[leaf.path];
                    const markerEdit = markerEdits[leaf.path];
                    const editDisplay = markerEdit ? markerEdit.display : htmlModel?.editDisplay;
                    const editTagMap = markerEdit ? markerEdit.tagMap : htmlModel?.editTagMap;
                    const editEntityMap = markerEdit ? markerEdit.entityMap : htmlModel?.editEntityMap;
                    const lastRowClass = isLastRow ? ' json-leaf-cell--last' : '';

                    return (
                      <React.Fragment key={leaf.path}>
                        <div
                          className={`json-leaf-row json-leaf-cell json-leaf-cell--col1${lastRowClass}`}
                          style={{ gridRow: row, gridColumn: 1 }}
                          data-leaf-row={row}
                        >
                          <div className="json-leaf-key" title={leaf.path}>{leafKeyLabel(leaf.path)}</div>
                          <div className="json-leaf-field">
                            <div className="json-leaf-field-value">
                              <MarkerRichText
                                className="marker-rich-text"
                                displayWithMarkers={htmlModel.sourceDisplay}
                                tagMap={htmlModel.sourceTagMap}
                                entityMap={htmlModel.sourceEntityMap}
                              />
                            </div>
                            <CopyButton
                              value={leaf.value}
                              ariaLabel={`Copy source value for ${leaf.path}`}
                              className="review-pane-copy-btn json-leaf-field-copy-btn"
                            />
                          </div>
                        </div>

                        <div
                          className={`json-leaf-row json-leaf-cell json-leaf-cell--col2${lastRowClass}`}
                          style={{ gridRow: row, gridColumn: 2 }}
                          data-leaf-row={row}
                        >
                          <div className="json-leaf-key json-leaf-key--hidden" aria-hidden="true" title={leaf.path}>{leafKeyLabel(leaf.path)}</div>
                          <div className="json-leaf-field">
                            <div className="json-leaf-field-value">
                              {!aiVal ? <span className="json-leaf-empty">—</span> : (
                                <MarkerRichText
                                  className="marker-rich-text"
                                  displayWithMarkers={htmlModel.aiDisplay}
                                  tagMap={htmlModel.tagMap}
                                  entityMap={htmlModel.entityMap}
                                />
                              )}
                            </div>
                            <CopyButton
                              value={aiVal}
                              ariaLabel={`Copy AI value for ${leaf.path}`}
                              className="review-pane-copy-btn json-leaf-field-copy-btn"
                            />
                          </div>
                        </div>

                        <div
                          className={`json-leaf-row json-leaf-cell json-leaf-cell--col3${lastRowClass}`}
                          style={{ gridRow: row, gridColumn: 3 }}
                          data-leaf-row={row}
                        >
                          <div className="json-leaf-key json-leaf-key--hidden" aria-hidden="true" title={leaf.path}>{leafKeyLabel(leaf.path)}</div>
                          <div className={`json-leaf-field${leafModified ? ' json-leaf-field--modified' : ''}`}>
                            {readOnly ? (
                              <div className="json-leaf-field-value">
                                {editVal ? (
                                  <MarkerRichText
                                    className="marker-rich-text"
                                    displayWithMarkers={editDisplay}
                                    tagMap={editTagMap}
                                    entityMap={editEntityMap}
                                  />
                                ) : emptyReadOnlyValue}
                              </div>
                            ) : (
                              <MarkerEditableCell
                                displayWithMarkers={editDisplay}
                                tagMap={editTagMap}
                                entityMap={editEntityMap}
                                onChange={(display, tagMap, entityMap) => (
                                  handleLeafMarkerEdit(leaf.path, display, tagMap, entityMap)
                                )}
                                modified={leafModified}
                                disabled={editingDisabled}
                                ariaLabel={`Edit ${leaf.path}`}
                                lastEditHighlight={buildHighlightForPath(leaf.path)}
                              />
                            )}
                            <CopyButton
                              value={editVal}
                              ariaLabel={`Copy edited value for ${leaf.path}`}
                              className="review-pane-copy-btn json-leaf-field-copy-btn"
                            />
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* ---- Standard single-text review ---- */
              <>
              {request.source_path && (
                <div style={{ marginBottom: '0.5rem', padding: '0.4rem 0.6rem', background: '#f0f4ff', borderRadius: '6px', fontSize: '0.8rem', fontFamily: 'monospace', color: '#111', fontWeight: 700 }}>
                  <strong>JSON path:</strong> {request.source_path}
                </div>
              )}
              <div className="review-compare-grid">
                <section className="review-compare-col">
                  <div className="detail-label">Source Text ({getSourceLabel(request)})</div>
                  <div className="review-compare-sub" aria-hidden="true" />
                  <div className="source-text-box review-compare-panel">
                    {source || '-'}
                  </div>
                  <div className="review-pane-copy-bar">
                    <CopyButton
                      value={source}
                      ariaLabel="Copy Source Text"
                      className="review-pane-copy-btn"
                      showLabel
                    />
                  </div>
                </section>

                <section className="review-compare-col review-editor-col review-editor-col--ai">
                  <div className="detail-label">Translated Text ({getTargetLabel(request)})</div>
                  <div className="review-compare-sub">
                    <div className="review-editor-label">
                      Original AI Translation
                      <span className="review-editor-tag review-editor-tag--ai">AI</span>
                    </div>
                  </div>
                  <div className="review-editor-readonly review-compare-panel">
                    {originalAITranslation || '-'}
                  </div>
                  <div className="review-pane-copy-bar">
                    <CopyButton
                      value={originalAITranslation}
                      ariaLabel="Copy Original AI Translation"
                      className="review-pane-copy-btn"
                      showLabel
                    />
                  </div>
                </section>

                <section className="review-compare-col review-editor-col review-editor-col--edit">
                  <div className="detail-label">
                    {readOnly ? 'Current Translation' : 'Edit Translation'}
                    {(readOnly ? displayWasEdited : wasEdited) && (
                      <span className="review-editor-tag review-editor-tag--edited">Modified</span>
                    )}
                  </div>
                  <div className="review-compare-sub">
                    {!readOnly && (
                      <p className="review-editor-hint">
                        Update the translation as needed. Your changes will be saved when you approve.
                      </p>
                    )}
                  </div>
                  {readOnly ? (
                    <div className="review-editor-readonly review-compare-panel">
                      {savedTarget || emptyReadOnlyValue}
                    </div>
                  ) : (
                    <div className="review-compare-panel marker-edit-plain-wrap">
                    <MarkerEditableCell
                      displayWithMarkers={editedTarget}
                      tagMap={{}}
                      entityMap={{}}
                      onChange={(display) => {
                        lastTouchedPathRef.current = '$root';
                        setHasUnsavedChangesSinceDraft(true);
                        setEditedTarget(display);
                      }}
                      disabled={editingDisabled}
                      ariaLabel="Edit Translation"
                      lastEditHighlight={buildHighlightForPath('$root')}
                    />
                    </div>
                  )}
                  <div className="review-pane-copy-bar">
                    <CopyButton
                      value={readOnly ? savedTarget : editedTarget}
                      ariaLabel="Copy Edited Translation"
                      className="review-pane-copy-btn"
                      showLabel
                    />
                  </div>
                </section>
              </div>
              </>
            )}
            </>
          ) : (
            <div className="detail-row">
              <div className="detail-label">Source Text ({getSourceLabel(request)})</div>
              <div className="source-text-box">
                {source || '-'}
              </div>
            </div>
          )}

          {!(hasTarget && isReadyForReview) && (
            <div className="detail-row">
              <div className="detail-label">Translated Text ({getTargetLabel(request)})</div>
              {isRetryPending ? (
                <div className="retry-pending-box">
                  {statusMessage}
                </div>
              ) : (
                <div className="pending-translation-box">
                  {statusMessage || 'Translation not yet available. Please refresh.'}
                </div>
              )}
            </div>
          )}

          {hasTarget && isReadyForReview && (
            <>
              <div className="detail-row review-classification-section">
                <div className="review-classification-grid">
                  <div className="review-classification-field">
                    <label htmlFor={readOnly ? undefined : 'error-category'}>Error Category</label>
                    {readOnly ? (
                      <div className="review-editor-readonly review-readonly-inline">
                        {savedErrorCategory || emptyReadOnlyValue}
                      </div>
                    ) : (
                      <select
                        id="error-category"
                        className="form-select"
                        value={wasEdited ? errorCategory : ''}
                        onChange={(e) => setErrorCategory(e.target.value)}
                        disabled={!wasEdited || editingDisabled}
                        title={!wasEdited ? 'Required only when you edit the translation.' : undefined}
                      >
                        <option value="">Select category...</option>
                        {ERROR_CATEGORIES.map((cat) => (
                          <option key={cat.label} value={cat.label} title={cat.definition}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="review-classification-field">
                    <label htmlFor={readOnly ? undefined : 'severity'}>Severity</label>
                    {readOnly ? (
                      <div className="review-editor-readonly review-readonly-inline">
                        {savedSeverity || emptyReadOnlyValue}
                      </div>
                    ) : (
                      <select
                        id="severity"
                        className="form-select"
                        value={wasEdited ? severity : ''}
                        onChange={(e) => setSeverity(e.target.value)}
                        disabled={!wasEdited || editingDisabled}
                        title={!wasEdited ? 'Required only when you edit the translation.' : undefined}
                      >
                        <option value="">Select severity...</option>
                        {SEVERITIES.map((sev) => (
                          <option key={sev.label} value={sev.label} title={sev.definition}>
                            {sev.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {!readOnly && (
                  wasEdited ? (
                    <p className="review-classification-edit-hint">
                      Edits require Error Category and Severity before approval.
                    </p>
                  ) : (
                    <p className="review-classification-locked-hint">
                      Error Category and Severity are required only when you edit the translation.
                    </p>
                  )
                )}
              </div>

              {request.caller_notes && (
                <div className="detail-row">
                  <div className="detail-label">Caller Notes</div>
                  <div className="review-editor-readonly" style={{ background: '#f0f4ff', borderLeft: '3px solid #4472C4', padding: '0.5rem 0.75rem' }}>
                    {request.caller_notes}
                  </div>
                </div>
              )}

              <div className="detail-row">
                <div className="detail-label">Notes (optional)</div>
                {readOnly ? (
                  <div className="review-editor-readonly">
                    {savedNotes || emptyReadOnlyValue}
                  </div>
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes about this translation..."
                    disabled={editingDisabled}
                  />
                )}
              </div>
            </>
          )}

          {isError && statusMessage && (
            <div className="detail-row">
              <div className="detail-label">Error Message</div>
              <div className="error-message-box">
                {statusMessage}
              </div>
            </div>
          )}

          {request.fuzzy_similarity && (
            <div className="detail-row">
              <div className="detail-label">Fuzzy Match Similarity</div>
              <div className="detail-value">{request.fuzzy_similarity}%</div>
            </div>
          )}

        </div>

        {error && (
          <div className="error-banner" ref={errorBannerRef} role="alert" tabIndex={-1}>
            {error}
          </div>
        )}

        <div className="modal-footer modal-footer--review-actions">
          <button className="btn btn-secondary" onClick={handleClose}>
            Close
          </button>
          {hasTarget && isReadyForReview && !readOnly && onSaveDraft && (
            <div className="save-draft-action">
              <button
                type="button"
                className="btn btn-secondary save-draft-btn"
                onClick={handleSaveDraft}
                disabled={
                  draftSaving
                  || saving
                  || editingDisabled
                  || (isJsonContent ? sourceLeaves.length === 0 : !editedTarget.trim())
                }
              >
                {draftSaving ? (
                  <>
                    <span className="spinner"></span>
                    Saving draft...
                  </>
                ) : (
                  'Save Draft'
                )}
              </button>
              <div className="save-draft-hint">
                Saves your progress. Not added to Translation Memory.
              </div>
            </div>
          )}
          {hasTarget && isReadyForReview && !readOnly && (
            <div className="approve-action-group">
              <div className={`approve-save-split${dropdownOpen ? ' approve-save-split--open' : ''}`} ref={approveButtonRef}>
                <button
                  type="button"
                  className="btn btn-success approve-save-main"
                  onClick={handleApprove}
                  disabled={saving || draftSaving || (isJsonContent ? sourceLeaves.length === 0 : !editedTarget.trim()) || editingDisabled}
                >
                  <span className="approve-save-main-icon" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" />
                      <polyline points="7 3 7 8 15 8" />
                    </svg>
                  </span>
                  {saving ? (
                    <>
                      <span className="spinner"></span>
                      Saving...
                    </>
                  ) : (
                    approveLabel
                  )}
                </button>
                {onSaveAndLock && (
                  <button
                    type="button"
                    className="btn btn-success approve-save-chevron"
                    aria-label="More save options"
                    aria-expanded={dropdownOpen}
                    aria-haspopup="menu"
                    disabled={saving || draftSaving || (isJsonContent ? sourceLeaves.length === 0 : !editedTarget.trim()) || editingDisabled}
                    onClick={() => setDropdownOpen((open) => !open)}
                  >
                    {dropdownOpen ? '▴' : '▾'}
                  </button>
                )}
              </div>
              <ApproveSaveDropdown
                open={dropdownOpen}
                anchorRef={approveButtonRef}
                onClose={() => setDropdownOpen(false)}
                onApprove={handleApprove}
                onSaveAndLock={handleSaveAndLock}
                saving={saving}
                disabled={(isJsonContent ? sourceLeaves.length === 0 : !editedTarget.trim()) || editingDisabled || draftSaving}
                approveLabel={approveLabel}
                showSaveAndLock={Boolean(onSaveAndLock)}
              />
              <div className="approve-action-subheading">
                {approveSubtext}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RequestDetail;
